import React, { useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { useCountryStore } from "../../store/useCountryStore";
import { CountryModal } from "./CountryModal";
import type { Country, CountryStatus } from "../../types/country";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { CountryPagination } from "./CountryPagination";

export const CountriesManagement: React.FC = () => {
  const {
    countries,
    loading,
    isRefreshing,
    loadIfNeeded,
    refreshCountries,
    fetchCountryByCode,
    addCountry,
    updateCountry,
    removeCountry,
  } = useCountryStore();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | CountryStatus | "unassigned"
  >("active");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "delete">(
    "create",
  );
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [loadingCountryCode, setLoadingCountryCode] = useState<string | null>(
    null,
  );
  const [actionError, setActionError] = useState<string>("");
  const itemsPerPage = 10;

  const getStatusBadge = (status: CountryStatus | null | undefined) => {
    if (status === "active") {
      return "bg-emerald-100 text-emerald-800";
    }
    if (status === "inactive") {
      return "bg-amber-100 text-amber-800";
    }
    if (status === "deleted") {
      return "bg-rose-100 text-rose-800";
    }
    return "bg-gray-100 text-gray-700";
  };

  useEffect(() => {
    loadIfNeeded().catch(console.error);
  }, [loadIfNeeded]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    return countries
      .filter((country) => {
        if (statusFilter === "all") {
          return true;
        }
        if (statusFilter === "unassigned") {
          return !country.status;
        }
        return country.status === statusFilter;
      })
      .filter((country) => {
        if (!q) {
          return true;
        }
        const searchable =
          `${country.country_code} ${country.country_name}`.toLowerCase();
        return searchable.includes(q);
      })
      .sort((a, b) => a.country_name.localeCompare(b.country_name));
  }, [countries, search, statusFilter]);

  const totalCount = filteredCountries.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const paginatedCountries = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCountries.slice(start, start + itemsPerPage);
  }, [filteredCountries, currentPage, itemsPerPage]);

  const handleCreate = () => {
    setActionError("");
    setSelectedCountry(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const handleEdit = async (country: Country) => {
    setActionError("");
    setLoadingCountryCode(country.country_code);
    try {
      const fullCountry = await fetchCountryByCode(country.country_code);
      setSelectedCountry(fullCountry);
      setModalMode("edit");
      setModalOpen(true);
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to load country",
      );
    } finally {
      setLoadingCountryCode(null);
    }
  };

  const handleDelete = (country: Country) => {
    setActionError("");
    setSelectedCountry(country);
    setModalMode("delete");
    setModalOpen(true);
  };

  const handleSave = async (payload: Record<string, unknown>) => {
    setActionError("");
    try {
      if (modalMode === "create") {
        await addCountry(
          payload as {
            country_code: string;
            country_name: string;
            status?: "active" | "inactive" | "deleted";
            geom?: string | null;
          },
        );
      } else if (modalMode === "edit" && selectedCountry) {
        await updateCountry(selectedCountry.country_code, payload);
      } else if (modalMode === "delete" && selectedCountry) {
        await removeCountry(selectedCountry.country_code);
      }
      setModalOpen(false);
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to save country",
      );
      throw error;
    }
  };

  const handleRefresh = () => {
    setActionError("");
    refreshCountries().catch(console.error);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Countries</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage country boundaries and metadata
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 flex items-center gap-2"
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
          <button
            onClick={handleCreate}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Country
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by country code or name..."
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(
              e.target.value as "all" | CountryStatus | "unassigned",
            )
          }
          className="w-full md:w-52 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deleted">Deleted</option>
          <option value="unassigned">Unassigned</option>
        </select>
      </div>

      {actionError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {actionError}
        </div>
      )}

      {loading && countries.length === 0 ? (
        <div className="py-12 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedCountries.map((country) => (
                <tr key={country.country_code}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {country.country_code}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {country.country_name}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusBadge(country.status)}`}
                    >
                      {(country.status || "unassigned").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => void handleEdit(country)}
                        className="text-blue-600 hover:text-blue-800"
                        disabled={loadingCountryCode === country.country_code}
                      >
                        {loadingCountryCode === country.country_code
                          ? "Loading..."
                          : "Edit"}
                      </button>
                      <button
                        onClick={() => handleDelete(country)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {paginatedCountries.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    No countries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <CountryPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      <CountryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        country={selectedCountry}
        onSave={handleSave}
      />
    </div>
  );
};
